import { Injectable } from '@angular/core';

/**
 * Service for managing focus trap behavior in modal dialogs.
 * Provides functionality to trap keyboard focus within a container element,
 * ensuring accessibility compliance for modal interactions.
 */
@Injectable({
  providedIn: 'root',
})
export class FocusTrapService {
  private focusableElements: HTMLElement[] = [];
  private previousActiveElement: HTMLElement | null = null;
  private container: HTMLElement | null = null;

  setupFocusTrap(container: HTMLElement): void {
    this.container = container;
    this.updateFocusableElements();
  }

  private updateFocusableElements(): void {
    if (!this.container) return;

    const focusableSelectors = [
      'button:not([disabled])',
      '[href]',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ].join(',');

    // Get all potential focusable elements
    const allElements = Array.from(this.container.querySelectorAll<HTMLElement>(focusableSelectors));

    // Filter to only include visible and focusable elements
    this.focusableElements = allElements.filter((el) => {
      const style = window.getComputedStyle(el);
      const isVisible = style.display !== 'none' && style.visibility !== 'hidden' && style.opacity !== '0';

      const isAriaHidden = el.getAttribute('aria-hidden') === 'true';

      const tabIndex = el.getAttribute('tabindex');
      const hasNegativeTabIndex = tabIndex !== null && parseInt(tabIndex, 10) < 0;

      return isVisible && !isAriaHidden && !hasNegativeTabIndex;
    });

    // Ensure close buttons are always the last focusable elements
    const closeButtons = this.focusableElements.filter((el) => {
      const closeButtonComponent = el.closest('vetai-close-button');
      return closeButtonComponent !== null;
    });

    if (closeButtons.length > 0) {
      this.focusableElements = this.focusableElements.filter((el) => !closeButtons.includes(el));
      this.focusableElements.push(...closeButtons);
    }
  }

  trapFocus(event: KeyboardEvent): boolean {
    if (event.key !== 'Tab' || !this.container) {
      return false;
    }

    // Dynamically update focusable elements to include newly rendered content
    // (e.g., autocomplete dropdowns that are rendered after dialog opens)
    this.updateFocusableElements();

    if (this.focusableElements.length === 0) {
      return false;
    }

    const firstElement = this.focusableElements[0];
    const lastElement = this.focusableElements[this.focusableElements.length - 1];
    const activeElement = document.activeElement as HTMLElement;

    if (event.shiftKey) {
      // Shift + Tab: Move focus backward
      if (activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
        return true;
      }
    } else {
      // Tab: Move focus forward
      if (activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
        return true;
      }
    }

    return false;
  }

  focusFirst(fallbackElement?: HTMLElement): void {
    if (this.focusableElements.length > 0) {
      this.focusableElements[0].focus();
      return;
    }

    if (fallbackElement) {
      fallbackElement.focus();
    }
  }

  /**
   * Checks if the current active element is a close button.
   */
  isFocusOnCloseButton(): boolean {
    const activeElement = document.activeElement as HTMLElement;
    if (!activeElement) return false;

    // Check if the element is inside a close button component
    const closeButtonComponent = activeElement.closest('vetai-close-button');
    return closeButtonComponent !== null;
  }

  /**
   * Resets focus to the first element if it's currently on a close button.
   * This is useful after content changes to ensure focus is on the correct element.
   */
  resetFocusIfOnCloseButton(container: HTMLElement): void {
    if (this.isFocusOnCloseButton()) {
      this.setupFocusTrap(container);
      this.focusFirst(container);
    }
  }

  storePreviousActiveElement(): void {
    this.previousActiveElement = document.activeElement as HTMLElement;
  }

  restorePreviousFocus(): void {
    if (this.previousActiveElement) {
      this.previousActiveElement.focus();
      this.previousActiveElement = null;
    }
  }

  destroy(): void {
    this.focusableElements = [];
    this.previousActiveElement = null;
    this.container = null;
  }
}
